/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import CampaignIcon from "@mui/icons-material/Campaign";
import PropTypes from "prop-types";
import { getConfig } from "../../config";

export default function DeveloperAgreement({
  versionTitle = null,
  uploadedDate = null,
  pdfUrl = null,
  isNewAgreementAvailable = false,
}) {
  const config = getConfig() || {};
  const showHardcodedFallback = config.ENABLE_HARDCODED_DEVELOPER_AGREEMENT_FALLBACK !== false;
  
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", 
                    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day} ${year}`;
  };

  return (
    <>
      {/* Show version title at the top */}
      {versionTitle && (
        <div style={{ marginBottom: "16px" }}>
          <span style={{ fontSize: "14px", color: "#1f2937", fontWeight: "700" }}>
            {"Version: "}{versionTitle}
          </span>
        </div>
      )}

      {/* Show updated terms banner for NEW_UPDATE_AVAILABLE */}
      {isNewAgreementAvailable && (
        <div
          style={{
            padding: "6px 8px",
            backgroundColor: "#FFDF72",
            borderRadius: "4px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            height: "36px",
          }}
        >
          <CampaignIcon style={{ fontSize: "20px", color: "#000000" }} />{" "}
          <span style={{ fontSize: "14px", color: "#000000" }}>
            We have updated the terms. Please read through carefully &amp; learn more about what&apos;s changed.
          </span>
        </div>
      )}

      {/* Show updated date */}
      {uploadedDate && (
        <div style={{ marginBottom: "16px" }}>
          <span style={{ 
            fontSize: "12px", 
            fontWeight: "600",
            background: "linear-gradient(90deg, #5324D1 0%, #00B8C4 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            display: "inline-block"
          }}>
            {"⚠ UPDATED ON: "}{formatDate(uploadedDate)}
          </span>
        </div>
      )}
      {/* Display PDF if URL is provided, otherwise show static content based on config */}
      {pdfUrl ? (
        <div style={{ padding: "24px" }}>
          <div>
            <iframe
              src={pdfUrl}
              style={{
                width: "828px",
                height: "700px",
                border: "none",
              }}
              title="Developer Agreement PDF"
            />
          </div>
        </div>
      ) : (
        // Show hardcoded HTML only if fallback is enabled in config (defaults to true)
        showHardcodedFallback && (
        <div>
          <div>
            <p>
              <strong>HARMAN IGNITE PROVIDER DISTRIBUTION AGREEMENT</strong>
            </p>
          <p>
            THESE TERMS GOVERN THE USE AND DISTRIBUTION OF YOUR APPLICATIONS ON
            HARMAN&rsquo;S IGNITE PLATFORM AND ARE LEGALLY BINDING BETWEEN YOU
            AND HARMAN (UNLESS A SEPARATE OFFLINE AGREEMENT SIGNED BY BOTH
            PARTIES IS IN EXISTENCE).
          </p>
          <p>
            This Distribution Agreement is between &ldquo;Provider&rdquo;
            (&ldquo;You&rdquo; or &ldquo;Your&rdquo;) and Harman International
            Industries, Incorporated (&ldquo;Harman&rdquo;) with headquarters
            located at 400 Atlantic Street, Stamford, Connecticut 06901.
          </p>
          <p>
            <strong>Definitions</strong>
          </p>
          <p>
            &ldquo;Agreement&rdquo; means this Harman IGNITE Provider
            Distribution Agreement and all attachments and exhibits expressly
            referred to herein, including the Harman Guidelines that govern
            development, distribution, and use of Applications, and any
            permitted amendments or addendums thereto.
          </p>
          <p>
            &ldquo;Applicable Law(s)&rdquo; means all local, state and federal
            laws, court orders, and governmental rules and regulations
            applicable to the development, delivery, distribution, and use of
            Applications, wherever in the world the Applications are made
            available for use, including but not limited to, those specifically
            relevant to consumer protection, data protection, privacy, data
            security, location data, and to the installation of software on
            another person&rsquo;s computer or other property.
          </p>
          <p>
            &ldquo;Application&rdquo; means a program developed or adapted for
            use on IGNITE and related technology and products, including any and
            all software, software packages, content, interfaces, digital
            materials, and any other items and services made available through
            the program. An Application includes any updates, enhancements,
            fixes, releases, and versions of the program.
          </p>
          <p>
            &ldquo;Brand Features&rdquo; means the trade names, trademarks,
            service marks, logos, domain names, and other distinctive brand
            features owned or licensed by Provider and/or Harman.
          </p>
          <p>
            &ldquo;End User&rdquo; means a person who can access or interact
            with IGNITE in a vehicle.
          </p>
          <p>
            &ldquo;Harman Guidelines&rdquo; means the guidelines,
            specifications, and policies as produced and amended by Harman from
            time to time, the current versions are set forth at: &middot;
          </p>
          <ul>
            <li>
              Distribution policy:{" "}
              <a href="https://ignitedevelopers.harman.com/ignite-program-policy-guidelines">
                https://ignitedevelopers.harman.com/ignite-program-policy-guidelines
              </a>;
            </li>
            <li>
              Technical Guidelines:{" "}
              <a href="https://ignitedevelopers.harman.com/migration-to-ignite-store">
                https://ignitedevelopers.harman.com/migration-to-ignite-store
              </a>;
            </li>
            <li>
              Harman Logo Guidelines:{" "}
              <a href="https://ignitedevelopers.harman.com/logo-usage-guidelines">
                https://ignitedevelopers.harman.com/logo-usage-guidelines
              </a>{"; and"}
            </li>
            <li>
              Certification:{" "}
              <a href="https://ignitedevelopers.harman.com/app-submission-process-for-ignite-store">
                https://ignitedevelopers.harman.com/app-submission-process-for-ignite-store
              </a>.
            </li>
          </ul>
          <p>
            &ldquo;IGNITE&rdquo; means Harman&rsquo;s Automotive Cloud platform,
            portal, and store, which is a solution that allows OEM Customers to
            select and offer 3rd party apps and services to End Users on a
            vehicle infotainment system, including any updates, modifications,
            extensions, re-releases, and improvements.
          </p>
          <p>
            &ldquo;Intellectual Property Rights&rdquo; means all patent rights,
            copyrights, trademarks, trade secrets, database rights, moral
            rights, know-how, and any other intellectual property rights
            (registered or unregistered) throughout the world, now in existence
            or developed during the Term.
          </p>
          <p>
            &ldquo;OEM Customer&rdquo; means a vehicle manufacturer testing or
            using IGNITE to make Applications available to End Users on a
            vehicle infotainment system.
          </p>
          <p>
            &ldquo;Personal Information&rdquo; means information that
            identifies, describes, and is reasonably capable of being associated
            with, or could reasonably be linked, directly or indirectly, with an
            individual or household.
          </p>
          <p>
            &ldquo;Representatives&rdquo; include Harman&rsquo;s contractors,
            suppliers, affiliates, third-party service providers and any other
            person Harman appoints to act on its behalf and the employees of the
            foregoing.
          </p>
          <p>
            <strong>1. Purpose </strong>
          </p>
          <p>
            Provider desires to make Application(s) available through the IGNITE
            platform, through which End Users will be able to download and use
            Applications directly on a vehicle infotainment system. Harman is
            willing to assess inclusion of Your Application on the platform
            under the terms and conditions set forth in this Agreement. You
            acknowledge that Harman may act as a platform service provider to
            make Your Applications available for viewing, download, use,
            interaction, and/or purchase via IGNITE. Unless the parties agree
            otherwise in an amendment, no fee will be charged for the
            distribution of Your Application.
          </p>
          <p>
            <strong>2. Harman Rights </strong>
          </p>
          <p>
            2.1. Harman has no obligation to distribute any Applications that
            You submit unless and until an Application has been certified to
            Harman's satisfaction in Harman&rsquo;s sole discretion.
          </p>
          <p>
            2.2. Harman may terminate or disable IGNITE or discontinue owning or
            operating IGNITE without any notice or liability to Provider
          </p>
          <p>
            2.3. Harman may provide or afford Provider electronic access to
            information, documents, and tools, including Harman or
            OEM/third-party documentation (together, &ldquo;Materials&rdquo;).
            The Materials and access thereto may be changed, improved, updated,
            or modified in Harman&rsquo;s sole discretion.
          </p>
          <p>
            2.4. Harman does not guarantee to promote or display Your
            Application in any location or placement within IGNITE and reserves
            all rights thereto. Harman has no obligation for any maintenance or
            support, technical or otherwise. While Harman may provide advance
            notice about material changes in its sole discretion, Harman has the
            right to improve, change, or modify IGNITE, Harman Guidelines, and
            all requirements, policies, and procedures without any liability to
            You.
          </p>
          <p>
            <strong>3. Provider Representations and Obligations</strong>
          </p>
          <p>
            3.1. You are solely responsible and liable for the Applications You
            submit. Provider is solely responsible for, and acknowledges that
            Harman has no participation in, controlling:
          </p>
          <p>(a) the content of Your Applications,</p>
          <p>(b) the security of Your Applications,</p>
          <p>(c) the functionality of Your Applications,</p>
          <p>(d) the pricing for Your Applications; and</p>
          <p>
            (e) compliance with any reporting required by Applicable Law related
            to Your Applications.
          </p>
          <p>3.2. Provider certifies to Harman and agrees that:</p>
          <p>
            (a) Provider has the right and authority to enter into this
            Agreement with full authority to legally bind its company or
            organization to the terms and obligations of this Agreement.
          </p>
          <p>
            (b) Provider will comply and maintain compliance with the Harman
            Guidelines and all Applicable Law and will obtain all necessary
            consents in accordance with Applicable Law related to Your
            Applications.
          </p>
          <p>
            (c) Provider is responsible for all its own costs and expenses,
            including taxes, losses and liabilities, in connection with its use
            of IGNITE and any activities that Provider undertakes to develop an
            Application, or otherwise undertakes in connection with IGNITE or
            with this Agreement.
          </p>
          <p>
            (d) Provider owns the necessary rights or has authority to grant the
            rights that Provider provides to Harman under this Agreement and
            Provider&rsquo;s agreement to these terms does not violate any
            existing obligations Provider has under any other agreement.
          </p>
          <p>
            (e) Provider is responsible for maintaining the confidentiality of
            any developer credentials (including any username/password) for
            IGNITE and shall not allow others to access IGNITE through its
            account.
          </p>
          <p>
            (f) Provider shall not use IGNITE for any activity outside of its
            intended purpose and thus shall not (i) copy, reproduce, distribute,
            decompile, reverse engineer, or disassemble IGNITE or Materials
            except as expressly permitted under this Agreement; (ii) claim
            ownership in IGNITE or any Materials; (iii) distribute, encumber,
            sell, rent, lease, sublicense, or otherwise transfer, publish or
            disclose IGNITE or any Materials to any third party; (iv) use IGNITE
            or Materials to aid in the violation of any third-party Intellectual
            Property Rights; or (v) attempt to circumvent or disable IGNITE or
            any technology features or measures in IGNITE, including without
            limitation, any access controls or copyright protection mechanisms.
          </p>
          <p>
            (g) Provider will ensure that its Applications do not contain
            viruses; hidden files, programs, or content; automatically
            replicating, transmitting, or activating computer program; malicious
            programs (e.g., trap doors, worms, Trojans, or time bombs); or any
            program that limits normal operation of IGNITE.
          </p>
          <p>
            (h) Provider will not use IGNITE resources or otherwise engage in
            any activity that interferes with the operation of IGNITE or that
            disrupts, damages, or accesses in an unauthorized manner the
            devices, servers, software, networks, or other services of Harman,
            OEM Customers, or any third party.
          </p>
          <p>
            (i) Provider hereby acknowledges that Provider, not Harman, is
            responsible for addressing any third-party claims relating to its
            Applications, including, but not limited to, any civil claim or any
            claim that an Application fails to conform to any legal or
            regulatory requirement such as consumer protection law.
          </p>
          <p>
            (j) Provider will ensure that its Applications do not contain, link
            to, access, provide or download any harmful, libelous, or defamatory
            content or materials. Harman does not undertake an obligation to
            monitor any Applications. If Harman becomes aware of and determines
            in its sole discretion that an Application creates potential
            liability and/or may adversely impact Harman, its OEM Customers, or
            End Users, Harman reserves the right to remove, reject, suspend, or
            limit the visibility of the Application.
          </p>
          <p>
            (k) Your Applications will meet all requirements of Harman and its
            OEM Customers that are communicated to You in writing. Provider will
            monitor its use of IGNITE and keep itself in full compliance with
            this Agreement, including any new requirements from Harman or its
            OEM Customers.
          </p>
          <p>3.3. Compliance with Third Party Rights.</p>
          <p>
            (a) Provider&rsquo;s Applications and all information and content
            provided or used with or through its Applications shall not violate,
            misappropriate, or infringe any third-party Intellectual Property
            Rights, rights of privacy and publicity, or other proprietary or
            legal rights (e.g. musical composition or performance rights, video
            rights, photography or image rights, logo rights, or third-party
            data rights).
          </p>
          <p>
            (b) Provider must comply with all obligations under any third-party
            licenses, including open source licenses, for any third-party
            components or content that You incorporate or use in connection with
            the development or use of Your Applications. Provider shall abide by
            the terms and conditions of any linked service or website. Provider
            shall only use, link to or access third-party software and content
            (e.g., video, photographs, artwork, music, social media content,
            maps, news, sports, financial data) for which Provider owns or has
            permission or rights pursuant to a written agreement.
          </p>
          <p>
            (c) Provider will obtain and maintain all necessary rights,
            clearances, consents, and licenses, and pay any fees associated with
            using, providing, or linking to any third-party content,
            information, materials or software through its Applications.
            Provider agrees that it is solely responsible to, and that Harman
            has no responsibility to, any third party for such third-party
            content, information, materials, or software.
          </p>
          <p>
            3.4. Accuracy. The information you provide to Harman under or in
            connection with this Agreement is true, accurate, current, and
            complete. Provider warrants that its Applications will operate and
            perform the functionality disclosed to Harman. Any documentation,
            advertisements, or marketing in connection with Your Application
            shall accurately describe the Application and shall not make any
            false or misleading statements. Provider will not materially change
            the Application without re-submitting the modified Application to
            Harman for review. Provider takes sole responsibility for its
            Applications that are uploaded to IGNITE, and for providing
            information and support to Harman, OEM Customers and End Users that
            is necessary for its Applications to function within IGNITE.
          </p>
          <p>
            3.5. Support. Provider accepts responsibility and liability, and
            agrees that Harman has neither liability nor responsibility, to
            undertake or handle support, complaints, and maintenance for
            Provider&rsquo;s Applications. Provider will provide accurate
            contact information and clear instructions for OEM Customers and End
            Users to contact Provider for customer support and legal purposes
            that must be displayed in each Application. Provider agrees to
            respond to all End User customer support inquiries within five (5)
            business days whenever Harman or an OEM Customer forwards an inquiry
            or complaint to Provider.
          </p>
          <p>
            3.6. Security. Provider shall implement industry standard security
            measures designed to prevent unauthorized access or use of
            Applications and IGNITE, and to prevent unauthorized access to or
            use of any system, network, data or other information and materials.
          </p>
          <p>
            3.7. Suspension. Provider understands that Harman and its OEM
            Customers have the right to immediately suspend Applications or
            access to IGNITE without any notice or liability to Provider. Harman
            will communicate a problem in advance of suspension if reasonably
            possible in Harman&rsquo;s discretion and, if not, will provide a
            written explanation of the suspension within five (5) business days
            after such emergency suspension. Example reasons for suspension of
            an Application include if it: (a) does not comply with Harman
            Guidelines, this Agreement or Applicable Law; (b) does not function
            properly; (c) has materially changed without review of those changes
            by Harman; (d) has a virus or bug; (e) disrupts operation of IGNITE
            or an OEM system; or (f) does not meet security requirements.
          </p>
          <p>
            3.8. If Provider discovers any violation of this Agreement, You must
            immediately notify Harman and, without limiting any rights or
            remedies of Harman, cooperate with Harman to address such violation.
          </p>
          <p>
            <strong>4. Authorizations and Rights</strong>
          </p>
          <p>
            4.1. Harman obtains no right, title, or interest from Provider under
            this Agreement to any of Your Applications or Your Intellectual
            Property Rights, except for the license rights herein granted by
            Provider.
          </p>
          <p>
            4.2. Provider grants Harman and its Representatives a non-exclusive,
            irrevocable, sublicensable, and royalty-free right and license
            during the Term to evaluate, distribute, reproduce, test, and use
            Your Applications worldwide in connection with the operation,
            distribution, and marketing of IGNITE and the in-vehicular use of
            Your Applications. This license is sublicensable by Harman to its
            OEM Customers.
          </p>
          <p>
            4.3. Provider grants to End Users, a non-exclusive, worldwide, and
            perpetual license to use its Applications downloaded through IGNITE.
          </p>
          <p>
            <strong>5. Brand Features and Publicity</strong>
          </p>
          <p>
            5.1. Except as expressly provided in this Agreement, neither party
            grants any right, title, or interest in any of the party&rsquo;s
            Brand Features to the other party.
          </p>
          <p>
            5.2. Provider grants to Harman, Harman Representatives, and OEM
            Customers, a nonexclusive, worldwide, royalty-free license during
            the Term to use and display Provider&rsquo;s Brand Features solely
            within IGNITE or on systems deploying IGNITE in connection with the
            distribution, use, and sale of its Applications via IGNITE.
          </p>
          <p>
            5.3. In addition to the license granted in Section 5.2 above, for
            purposes of marketing Applications in connection with IGNITE,
            Provider agrees that Harman, Harman Representatives, and its OEM
            Customers may include visual elements from Your Applications
            (including characters and video) and Your Brand Features (a) within
            IGNITE; (b) in online, mobile, television, out of home (e.g.
            billboard), and print advertising formats outside IGNITE; (c) in
            presentations or announcements of Application availability on
            IGNITE; and (d) in lists which appear online or on Harman systems
            such as application or customer lists.
          </p>
          <p>
            5.4. Harman grants to Provider a nonexclusive, worldwide,
            royalty-free license to use the Harman Brand Features for the Term
            solely for marketing the Provider&rsquo;s Applications in connection
            with IGNITE in accord with the guidelines at:{' '}
            <a href="https://ignitedevelopers.harman.com/logo-usage-guidelines">
              https://ignitedevelopers.harman.com/logo-usage-guidelines.
            </a>
          </p>
          <p>
            <strong>6. Confidentiality</strong>
          </p>
          <p>
            6.1. This Agreement and all Confidential Information shall be held
            confidential and used only as permitted in this Agreement or as
            otherwise expressly agreed. The parties shall not misuse or disclose
            to any third party any Confidential Information of the other party
            except as permitted hereunder.
          </p>
          <p>
            &ldquo;Confidential Information&rdquo; means all non-public
            information designated, either in writing or verbally, as
            confidential, or which, under the circumstances of disclosure ought
            to be treated as confidential, including, but not limited to, any
            technical information, technology, benchmarking, products, software,
            applications, prototypes, ideas, inventions, improvements, sales
            data, customer identity, personnel information, marketing plans,
            business strategies or practices, financial information, cost or
            pricing information, know-how, or other similar data or information,
            and all copies and tangible embodiments thereof. Confidential
            Information does not include information known prior to the
            disclosure or information that becomes publicly available through no
            fault of the non-disclosing party.
          </p>
          <p>
            <strong>7. Data Privacy</strong>
          </p>
          <p>
            7.1. Provider acknowledges and agrees that You, and not Harman, are
            the data controller or business (as such terms are defined in
            applicable privacy laws) with respect to any Personal Information
            that You collect and use via Your Applications. Provider accepts all
            responsibility for compliance with Applicable Law related to data
            privacy and data security regarding Your Applications.
          </p>
          <p>
            7.2. Provider must provide appropriate notice and obtain any
            required consent before collecting, transmitting, or using Personal
            Information involving Your Applications. Provider expressly
            acknowledge that Harman is not warranting to You that an End User
            has given any consent to the processing of their data by Provider.
            Provider is responsible for responding to End User requests to
            exercise their privacy rights (which may include the right of
            deletion, the right of correction right to opt-out of sales, etc.)
            under Applicable Law.
          </p>
          <p>
            7.3. The parties acknowledge and agree that neither party processes
            Personal Information on behalf of the other party.
          </p>
          <p>
            7.4. Harman may collect data related to IGNITE&rsquo;s operations
            (such as application downloads and duration of use) and may make
            this information available to Provider over the portal in anonymized
            and/or de-identified form.
          </p>
          <p>
            <strong>8. Information and Feedback</strong>
          </p>
          <p>
            8.1. You have no obligation to provide feedback, suggestions, or
            comments with respect to IGNITE (collectively,
            &ldquo;Feedback&rdquo;) but, if You do voluntarily provide Feedback,
            You agree that all Feedback shall be owned by Harman and You hereby
            grant Harman all rights, title and ownership of such Feedback
            (including Intellectual Property Rights therein). Harman may use the
            Feedback with no obligation of any kind to You. Harman&rsquo;s right
            to use Feedback will survive the expiration or termination of this
            Agreement.
          </p>
          <p>
            <strong>9. Indemnity</strong>
          </p>
          <p>
            Provider agrees to indemnify, defend, and hold harmless Harman, its
            direct and indirect customers, including OEM Customers and Harman
            Representatives, from and against any and all losses, damages,
            injuries (including death), causes of action, claims, penalties,
            interest, additional taxes, demands and expenses, including
            reasonable legal fees and expenses, from any claim or allegation of
            a third party relating to: (i) Your failure to comply with this
            Agreement; (ii) infringement or misappropriation by Your Application
            of Intellectual Property Rights, rights to privacy or publicity, or
            other proprietary rights; (iii) data security breaches caused by You
            or Your Application; (iv) Your failure to comply with any Applicable
            Law; and/or (v) the operation or performance of Your Applications.
          </p>
          <p>
            <strong>10. Term and Termination</strong>
          </p>
          <p>
            10.1. The Effective Date of this Agreement shall be the day that You
            accept it on the Portal. This Agreement shall extend one year from
            the Effective Date (the &ldquo;Initial Term&rdquo;) and shall
            automatically renew for additional, consecutive one (1) year periods
            (each a &ldquo;Renewal Term&rdquo;), unless sooner terminated in
            accordance with this Agreement. The Initial Term and any Renewal
            Terms shall collectively constitute the &ldquo;Term&rdquo;.
          </p>
          <p>
            10.2. Either Party may terminate this Agreement at any time upon
            thirty (30) days&rsquo; written notice. Harman may also terminate
            this Agreement immediately if Provider: (i) breaches or takes any
            action inconsistent with this Agreement, the Harman Guidelines, or
            any rules and policies set forth by Harman or OEM customers; (ii)
            files any lawsuit against Harman or its customers; (iii) engages in,
            encourages, or participates in any unlawful, deceitful or fraudulent
            conduct; (iv) becomes insolvent or files for bankruptcy or becomes
            subject to any similar proceeding; or (v) fails to agree with new
            rules or policies as made available to Provider; or if (vii) Harman
            terminates its use of IGNITE.
          </p>
          <p>
            10.3. Neither party shall have any liability to the other for
            termination of this Agreement.
          </p>
          <p>
            10.4. After termination or expiration, Provider shall 1) unpublish
            its Applications; 2) cease its use of IGNITE; 3) stop using all
            Harman Brand Features; and 4) return or destroy all Confidential
            Information (and provide a written certification of the destruction
            if requested by Harman).
          </p>
          <p>
            10.5. After termination or expiration of this Agreement, Harman may
            continue to distribute the App to OEM Clients and End Users up to a
            maximum period of ten (10) days thereafter and may retain and use
            copies of the Applications for support of IGNITE and continue to
            make maintenance and support requests
          </p>
          <p>
            to Provider for End Users that downloaded the Application during the
            Term for ninety (90) days thereafter (the &ldquo;Wind-Down
            Period&rdquo;).
          </p>
          <p>
            10.6.<u>Survival.</u> All obligations related in any way to
            confidentiality or indemnity shall survive termination or expiration
            of this Agreement and sections 2, 3.3, 4, 6, 7, 8, 9 and 11 shall
            also survive.
          </p>
          <p>
            <strong>
              11. NO WARRANTY BY HARMAN AND LIMITATION OF LIABILITY
            </strong>
          </p>
          <p>
            11.1. HARMAN MAKES NO REPRESENTATIONS, CONDITIONS OR WARRANTIES IN
            RESPECT OF IGNITE OR ANY OTHER SERVICE OR MATERIALS. IGNITE AND ANY
            MATERIALS ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
            AVAILABLE&rdquo; INCLUDING WITH ALL FAULTS AND ERRORS AS MAY OCCUR
            THEREIN. HARMAN, ON BEHALF OF ITSELF AND ALL HARMAN REPRESENTATIVES,
            EXPLICITLY DISCLAIMS ALL WARRANTIES AND CONDITIONS, EXPRESS OR
            IMPLIED, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY,
            NON-INFRINGEMENT, ACCURACY, SATISFACTORY QUALITY AND FITNESS FOR A
            PARTICULAR PURPOSE IN RESPECT OF IGNITE, ANY SERVICES, OR ANY
            MATERIALS.
          </p>
          <p>
            11.2. HARMAN DOES NOT PROVIDE ANY WARRANTIES REGARDING THE
            AVAILABILITY, PERFORMANCE OR OPERATION OF IGNITE OR ANY MATERIALS.
            HARMAN DOES NOT WARRANTY THAT IGNITE WILL OPERATE WITHOUT
            INTERRUPTION OR BE ERROR FREE.
          </p>
          <p>
            11.3. HARMAN DOES NOT WARRANT THAT IGNITE OR ANY MATERIALS WILL
            OPERATE PROPERLY WITH YOUR APPLICATIONS AND DOES NOT WARRANT THAT
            FUTURE OPERATION OF IGNITE WILL BE COMPATIBLE WITH CURRENT OPERATION
            OR APPLICATIONS. YOU WILL BE RESPONSIBLE FOR ALL REPAIRS TO YOUR
            APPLICATIONS AND ANY OTHER LOSSES ACTUALLY OR ALLEGEDLY CAUSED BY
            IGNITE.
          </p>
          <p>
            11.4. HARMAN DOES NOT GUARANTEE OR WARRANT THE ACCURACY OF ANY DATA
            MADE AVAILABLE TO YOU THROUGH IGNITE.
          </p>
          <p>
            11.5. TO THE EXTENT THAT ANY JURISDICTIONS LIMIT OR DISALLOW THE
            DISCLAIMER OF IMPLIED OR OTHER WARRANTIES, THE DISCLAIMERS SHALL
            APPLY TO THE FULL EXTENT PERMITTED UNDER LAW.
          </p>
          <p>
            11.6. TO THE EXTENT ALLOWED BY LAW, HARMAN WILL NOT BE LIABLE FOR
            ANY INDIRECT, SPECIAL, PUNITIVE OR CONSEQUENTIAL DAMAGES OR AMOUNTS
            FOR LOSS OF INCOME, PROFITS OR SAVINGS ARISING OUT OF OR RELATED TO
            THIS AGREEMENT, IGNITE OR YOUR APPLICATION. THESE LIMITS AND
            EXCLUSIONS APPLY EVEN IF HARMAN KNEW OR SHOULD HAVE KNOWN ABOUT THE
            POSSIBILITY OF THE DAMAGES AND WHETHER ARISING BASED ON NEGLIGENCE,
            TORT, OR BREACH OF CONTRACT. TO THE EXTENT ALLOWED BY LAW, THE
            MAXIMUM LIABILITY OF HARMAN AND ITS REPRESENTATIVES FOR ALL CLAIMS
            IN THE AGGREGATE UNDER THIS AGREEMENT SHALL BE NO GREATER THAN
            $1000.00.
          </p>
          <p>
            11.7. Because Harman provides service through third-party wired
            and/or wireless networks, Harman cannot promise that its
            communications will not be intercepted. Provider agrees that Harman
            will not be liable for any damages for any loss of privacy occurring
            in communication over such networks.
          </p>
          <p>
            <strong>12. Changes to the Agreement.</strong>
          </p>
          <p>
            12.1. This Agreement may be modified only by a written amendment
            signed by both parties.
          </p>
          <p>
            <strong>13. General Provisions</strong>
          </p>
          <p>
            13.1. <u>Relationship</u>. Provider agrees that Harman and Provider
            intend to create an independent contractor relationship, and nothing
            contained in this Agreement shall be construed to make either
            Provider or Harman, partners, joint ventures, principals, agents, or
            employees of the other. Neither party shall have any right, power or
            authority, express or implied, to bind the other.
          </p>
          <p>
            13.2. <u>Notices</u>. All notices, requests, consents, approvals,
            and other communications required or permitted under this Agreement
            must be in writing and sent by reasonable means to the parties as
            they may designate. Such notice will be considered given when
            delivered, or if delivery is not accomplished as a result of some
            action or inaction by the recipient, when tendered.
          </p>
          <table
            style={{ height: "347px", marginLeft: "auto", marginRight: "auto" }}
            border="1px"
          >
            <thead>
              <tr>
                <th width="312">
                  <p>
                    <strong>
                      <u>The mail and email address for Harman is:</u>
                    </strong>
                  </p>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td width="312">
                  <p>General Counsel</p>
                </td>
              </tr>
              <tr>
                <td width="312">
                  <p>Harman International Industries, Incorporated</p>
                </td>
              </tr>
              <tr>
                <td width="312">
                  <p>400 Atlantic St, Stamford, CT 06901</p>
                </td>
              </tr>
              <tr>
                <td width="312">
                  <p>Attn: LegalNotices@harman.com</p>
                </td>
              </tr>
            </tbody>
          </table>
          <p>
            The address, email, and contact information provided by You in the
            IGNITE portal shall serve as Your address for receipt of any notices
            or other legal communications under this Agreement. Either party may
            change its address for notification purposes upon giving notice to
            the other.
          </p>
          <p>
            13.3.<u>Severability</u>. If any provision of this Agreement, or of
            any document incorporated herein by reference is held by a court of
            competent jurisdiction to be contrary to law, then the remaining
            provisions of this Agreement or the application of such provision to
            persons or circumstances other than those as to which it is invalid
            or unenforceable shall not be affected thereby, and each such
            provision of this Agreement shall be valid and enforceable to the
            extent granted by law and such term or provision shall be deemed to
            be refined to comply with the Applicable Law while achieving its
            original intent as closely as possible. Notwithstanding the
            foregoing, if any right to Harman or any terms related to IGNITE
            under this Agreement is deemed contrary to law, then Harman may
            immediately terminate this Agreement.
          </p>
          <p>
            13.4.<u> Waiver. </u>No term or condition of this Agreement or of
            any document referenced herein or incorporated herein by reference
            shall be deemed waived and no breach shall be deemed excused unless
            such waiver or consent shall be in writing and signed by the party
            claimed to have waived or consented. No consent by any party to, or
            waiver of, a breach by the other, whether express or implied, shall
            constitute a consent to, waiver of, or excuse for any different or
            subsequent breach.
          </p>
          <p>
            13.5. <u>References and Headings</u>. In this Agreement, the section
            and paragraph headings are for convenience of reference only and
            shall not be considered in interpretation of this Agreement. Any
            phrase introduced by the word &ldquo;including&rdquo; or any similar
            words shall be construed as illustrative and shall not be given a
            restrictive meaning because they are followed by examples for
            general phrases. The term &ldquo;herein&rdquo; and any similar words
            shall be construed as referring to this Agreement in its entirety
            and not to any particular section except to the extent expressly
            specified.
          </p>
          <p>
            13.6.<u>Governing Law</u>. This Agreement shall be governed by the
            laws of the State of New York, except in relation to any conflict of
            law provisions. Provider and Harman consent to personal jurisdiction
            and exclusive venue in the state courts located in New York County
            or the federal courts of the Southern District of New York over any
            action or proceeding to enforce or defend any matter related to the
            Agreement and agrees not to institute any such legal action or
            proceeding in any other court (unless otherwise required by law).
            Provider agrees that, regardless of any statute or law to the
            contrary, it must file any claim or cause of action related to this
            Agreement or IGNITE within one (1) year after such claim or cause of
            action arose, or You shall be forever barred from filing such claim
            or cause of action.
          </p>
          <p>
            13.7. <u>Entire Agreement</u>. This Agreement is the entire
            Agreement between the parties with respect to its subject matter,
            and there are no other representations, understandings, or
            agreements between the parties relative to such subject matter.
          </p>
          <p>
            13.8. <u>Counterparts</u>. This Agreement may be executed in
            counterparts, each of which will be deemed an original, and together
            constitute one and the same instrument. Signing and transmission of
            this Agreement by facsimile or electronic document transfer will be
            acceptable and binding.
          </p>
          <p>
            13.9. <u>Assignment</u>. Neither party may assign any of its rights
            or obligations under this Agreement without the prior written
            consent of the other party, except that either party may assign any
            or all of its rights or obligations under this Agreement, without
            such consent (a) to any affiliate; (b) in connection with any
            merger, consolidation, reorganization, sale of all
            or&nbsp;substantially all of its related Assets, a spin-off
            involving IGNITE, or similar transaction.
          </p>
        </div>
      </div>
        )
      )}
    </>
  );
}

DeveloperAgreement.propTypes = {
  versionTitle: PropTypes.string,
  uploadedDate: PropTypes.string,
  pdfUrl: PropTypes.string,
  isNewAgreementAvailable: PropTypes.bool,
};